/**
 * WHOPhaseCard.js
 * Renders a single WHO Surgical Safety Checklist phase with its specific
 * checklist items, N/A toggles, confirmation checkboxes, and completion logic.
 */
import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Chip from '@material-ui/core/Chip';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import { ChecklistItem } from '../shared';
import { WHO_PHASE, WHO_PHASE_LABELS } from '../shared/constants';

const PHASE_ITEMS = {
  [WHO_PHASE.SIGN_IN]: [
    { key: 'patient_identity_confirmed', label: 'Patient identity confirmed (name, DOB, wristband)', required: true, showNote: false },
    { key: 'site_marked', label: 'Surgical site marked and visible', required: true, showNote: false },
    { key: 'anaesthesia_check', label: 'Anaesthesia safety check completed', required: true, showNote: false },
    { key: 'allergies_checked', label: 'Allergies checked', required: true, showNote: true },
    { key: 'pulse_oximeter_attached', label: 'Pulse oximeter attached and functioning', required: true, showNote: false },
    { key: 'known_allergy_display', label: 'Known allergy displayed on patient chart', required: false, showNote: true, allowNA: true },
  ],
  [WHO_PHASE.TIME_OUT]: [
    { key: 'team_introduced', label: 'All team members have introduced themselves by name and role', required: true, showNote: false },
    { key: 'procedure_confirmed', label: 'Patient name, procedure, and incision site confirmed', required: true, showNote: false },
    { key: 'antibiotics_given', label: 'Prophylactic antibiotics given within the last 60 minutes', required: true, showNote: true, allowNA: true },
    { key: 'anticipated_critical_events', label: 'Anticipated critical events reviewed', required: true, showNote: true },
    { key: 'imaging_displayed', label: 'Essential imaging displayed', required: false, showNote: false, allowNA: true },
    { key: 'equipment_sterility', label: 'Equipment sterility confirmed (including indicator results)', required: true, showNote: false },
  ],
  [WHO_PHASE.SIGN_OUT]: [
    { key: 'instrument_count_correct', label: 'Instrument, sponge, and needle counts are correct', required: true, showNote: true },
    { key: 'specimen_labelled', label: 'Specimen(s) labelled correctly', required: false, showNote: true, allowNA: true },
    { key: 'concerns_addressed', label: 'Any equipment problems or concerns addressed', required: true, showNote: true },
    { key: 'equipment_issues', label: 'Key equipment issues noted for follow-up', required: false, showNote: true, allowNA: true },
    { key: 'recovery_plan_confirmed', label: 'Recovery plan and key concerns reviewed with team', required: true, showNote: false },
  ],
};

const useStyles = makeStyles(() => ({
  root: {
    padding: 20,
    border: '1px solid #e0e0e0',
    borderRadius: 8,
  },
  disabledOverlay: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  phaseHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  phaseTitle: {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: '#333',
  },
  phaseChip: {
    fontWeight: 500,
  },
  description: {
    fontSize: '0.85rem',
    color: '#666',
    marginBottom: 4,
  },
  leadRole: {
    fontSize: '0.8rem',
    color: '#1976D2',
    fontWeight: 500,
    marginBottom: 16,
  },
  itemsContainer: {
    marginTop: 12,
    marginBottom: 16,
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  naToggle: {
    fontSize: '0.75rem',
    color: '#999',
    cursor: 'pointer',
    marginLeft: 8,
    padding: '2px 8px',
    borderRadius: 4,
    border: '1px solid #ddd',
    backgroundColor: '#fafafa',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
  naToggleActive: {
    backgroundColor: '#FFF3E0',
    borderColor: '#FF9800',
    color: '#E65100',
  },
  confirmSection: {
    marginTop: 16,
    padding: 16,
    backgroundColor: '#FAFAFA',
    borderRadius: 8,
    border: '1px solid #E0E0E0',
  },
  confirmTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#333',
    marginBottom: 8,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 20,
    paddingTop: 16,
    borderTop: '1px solid #e0e0e0',
  },
  progress: {
    fontSize: '0.85rem',
    color: '#666',
  },
  completedBanner: {
    padding: 16,
    backgroundColor: '#E8F5E9',
    borderRadius: 8,
    textAlign: 'center',
  },
}));

const WHOPhaseCard = ({
  phase,
  data = {},
  onSave,
  disabled = false,
  surgeryId,
  description,
  leadRole,
}) => {
  const classes = useStyles();
  const items = PHASE_ITEMS[phase] || [];

  const [checkedItems, setCheckedItems] = useState({});
  const [naItems, setNaItems] = useState({});
  const [notes, setNotes] = useState({});
  const [surgeonConfirmed, setSurgeonConfirmed] = useState(false);
  const [anaesthetistConfirmed, setAnaesthetistConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initialize from existing data
  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      const initialChecked = {};
      const initialNA = {};
      const initialNotes = {};
      items.forEach((item) => {
        if (data[item.key] === true) initialChecked[item.key] = true;
        if (data[`${item.key}_na`] === true) initialNA[item.key] = true;
        if (data[`${item.key}_note`]) initialNotes[item.key] = data[`${item.key}_note`];
      });
      setCheckedItems(initialChecked);
      setNaItems(initialNA);
      setNotes(initialNotes);
      if (data.surgeon_confirmed) setSurgeonConfirmed(true);
      if (data.anaesthetist_confirmed) setAnaesthetistConfirmed(true);
    }
  }, []); 

  const handleToggle = (key) => () => {
    if (naItems[key]) return;
    setCheckedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleNAToggle = (key) => () => {
    setNaItems((prev) => {
      const isNA = !prev[key];
      if (isNA) {
        setCheckedItems((c) => ({ ...c, [key]: false }));
      }
      return { ...prev, [key]: isNA };
    });
  };

  const handleNoteChange = (key) => (e) => {
    setNotes((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const isItemSatisfied = (item) => {
    if (naItems[item.key]) return true;
    if (item.required) return !!checkedItems[item.key];
    return true; // optional items always satisfied
  };

  const mandatoryItems = items.filter((i) => i.required);
  const satisfiedCount = items.filter(isItemSatisfied).length;
  const allMandatorySatisfied = mandatoryItems.every(isItemSatisfied);
  const canComplete = allMandatorySatisfied && surgeonConfirmed && anaesthetistConfirmed;

  const handleComplete = async () => {
    if (!canComplete) return;
    setSaving(true);
    try {
      const payload = {};
      items.forEach((item) => {
        payload[item.key] = !!checkedItems[item.key];
        if (naItems[item.key]) payload[`${item.key}_na`] = true;
        if (notes[item.key]) payload[`${item.key}_note`] = notes[item.key];
      });
      payload.surgeon_confirmed = surgeonConfirmed;
      payload.anaesthetist_confirmed = anaesthetistConfirmed;
      payload.completed = true;
      payload.completed_at = new Date().toISOString();

      await onSave(payload);
    } catch (err) {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  if (data?.completed) {
    return (
      <Paper className={classes.root} elevation={0}>
        <div className={classes.phaseHeader}>
          <Typography className={classes.phaseTitle}>
            {WHO_PHASE_LABELS[phase]}
          </Typography>
          <Chip label="Completed" size="small" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }} />
        </div>
        <div className={classes.completedBanner}>
          <Typography variant="body2" style={{ color: '#2E7D32' }}>
            Phase completed{data.completed_by ? ` by ${data.completed_by}` : ''}{' '}
            {data.completed_at
              ? `at ${new Date(data.completed_at).toLocaleTimeString()}`
              : ''}
          </Typography>
        </div>
      </Paper>
    );
  }

  return (
    <Paper className={`${classes.root} ${disabled ? classes.disabledOverlay : ''}`} elevation={0}>
      <div className={classes.phaseHeader}>
        <Typography className={classes.phaseTitle}>
          {WHO_PHASE_LABELS[phase]}
        </Typography>
        <Chip
          label="In Progress"
          size="small"
          className={classes.phaseChip}
          style={{ backgroundColor: '#FFF3E0', color: '#E65100' }}
        />
      </div>
      {description && (
        <Typography className={classes.description}>{description}</Typography>
      )}
      {leadRole && (
        <Typography className={classes.leadRole}>{leadRole}</Typography>
      )}

      <Divider />

      <div className={classes.itemsContainer}>
        {items.map((item) => (
          <div key={item.key} className={classes.itemRow}>
            <div style={{ flex: 1 }}>
              <ChecklistItem
                label={item.label}
                checked={!!checkedItems[item.key]}
                onChange={handleToggle(item.key)}
                required={item.required && !naItems[item.key]}
                showNote={item.showNote}
                noteValue={notes[item.key] || ''}
                onNoteChange={handleNoteChange(item.key)}
                disabled={disabled || !!naItems[item.key]}
              />
            </div>
            {item.allowNA && (
              <span
                className={`${classes.naToggle} ${naItems[item.key] ? classes.naToggleActive : ''}`}
                onClick={disabled ? undefined : handleNAToggle(item.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && !disabled && handleNAToggle(item.key)()}
              >
                N/A
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Confirmation section */}
      <div className={classes.confirmSection}>
        <Typography className={classes.confirmTitle}>Phase Confirmation</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={surgeonConfirmed}
              onChange={(e) => setSurgeonConfirmed(e.target.checked)}
              color="primary"
              size="small"
              disabled={disabled}
            />
          }
          label={<Typography variant="body2">Confirmed by Surgeon</Typography>}
        />
        <br />
        <FormControlLabel
          control={
            <Checkbox
              checked={anaesthetistConfirmed}
              onChange={(e) => setAnaesthetistConfirmed(e.target.checked)}
              color="primary"
              size="small"
              disabled={disabled}
            />
          }
          label={<Typography variant="body2">Confirmed by Anaesthetist</Typography>}
        />
      </div>

      <div className={classes.footer}>
        <Typography className={classes.progress}>
          {satisfiedCount} of {items.length} items verified
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleComplete}
          disabled={!canComplete || saving || disabled}
        >
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Complete Phase'}
        </Button>
      </div>
    </Paper>
  );
};

export default WHOPhaseCard;
